// Withdrawal.tsx — single unified cashout form (no tabs).
// The wallet channel (Mobile Money or Card) is a first-step selector inside the
// same form. Once chosen, the source picker, currency and recipient flow adapt.
// Design language: Metronic 9, premium fintech feel.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  AlertCircle,
  ArrowRight,
  Banknote,
  CheckCircle2,
  ChevronRight,
  Clock,
  CreditCard,
  Hourglass,
  Loader2,
  RefreshCw,
  Settings,
  Shield,
  Smartphone,
  Wallet,
  XCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../contexts/LocalizationContext';
import { withdrawalService, type BalanceSummary, type UIWithdrawalStatus } from '../services/withdrawalService';
import { useWithdrawalEligibility } from '../hooks/useWithdrawalEligibility';
import { subscriptionService } from '../services/subscriptionService';

type Channel = 'mobile' | 'card';
type Operator = 'mpesa' | 'airtel' | 'orange' | 'afrimoney';
type Currency = 'CDF' | 'USD';
type FlowStep = 'form' | 'otp' | 'done';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3944';
const UNIFIED_API = import.meta.env.VITE_UNIFIED_API_URL || 'https://cd.merchants.gofreshpay.com/api/backoffice';

const OPERATOR_LABELS: Record<Operator, string> = {
  mpesa: 'M-Pesa',
  airtel: 'Airtel Money',
  orange: 'Orange Money',
  afrimoney: 'Afrimoney',
};

const OPERATOR_LOGOS: Record<Operator, string> = {
  mpesa: '/mpesa.png',
  airtel: '/airtel.png',
  orange: '/orange.png',
  afrimoney: '/afrimoney.png',
};

const OPERATORS: Operator[] = ['mpesa', 'airtel', 'orange', 'afrimoney'];

interface CardWalletRow { currency: Currency; balance: number; mode: string; }

interface HistoryItem {
  id: string;
  amount: number;
  currency: string;
  method: string;
  reference: string;
  status: UIWithdrawalStatus;
  created_at: string;
  // Populated by /withdrawal/history since 2026-07-14 — the actual wallet
  // debited under biggest-first, which may differ from `method` (the
  // requested operator) when the requested wallet was insufficient.
  source_wallets?: string[];
  debit_plan?: Array<{ method: string; amount: number }>;
}

function fmt(amount: number, currency: Currency): string {
  if (currency === 'CDF') {
    return new Intl.NumberFormat('fr-CD', {
      style: 'currency', currency: 'CDF',
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(amount).replace('CDF', 'FC');
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(amount);
}

function fmtDate(s: string | null): string {
  if (!s) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(s));
  } catch { return s; }
}

function mapStatus(raw?: string): UIWithdrawalStatus {
  const s = (raw || '').toLowerCase();
  if (['success', 'successful', 'succeeded', 'completed', 'paid'].includes(s)) return 'successful';
  if (['fail', 'failed', 'error', 'rejected', 'canceled', 'cancelled'].includes(s)) return 'failed';
  if (['processing', 'in_progress', 'pending_processing'].includes(s)) return 'processing';
  return 'pending';
}

const Withdrawal: React.FC = () => {
  const { t } = useLocalization();
  const { user } = useAuth();
  const { data: eligibility, isLoading: isLoadingEligibility } = useWithdrawalEligibility();

  // Access flags
  const [hasCardAccess, setHasCardAccess] = useState(false);
  const [hasMobileAccess, setHasMobileAccess] = useState(false);
  const [accessLoading, setAccessLoading] = useState(true);
  const hasWithdrawalAccess = !!eligibility?.has_withdrawal_access;

  // Channel + form state
  const [channel, setChannel] = useState<Channel>('mobile');
  const [mobileOperator, setMobileOperator] = useState<Operator>('mpesa');   // source AND dest for mobile
  const [mobileCurrency, setMobileCurrency] = useState<Currency>('CDF');     // source currency for mobile
  const [cardCurrency, setCardCurrency] = useState<Currency>('CDF');         // source currency for card
  const [cardDestOperator, setCardDestOperator] = useState<Operator>('mpesa'); // dest for card cashout
  const [amount, setAmount] = useState('');

  // TELCO_FEE_V1 — Airtel 1.16% commission preview (computed server-side)
  type FeePreview = {
    amount: number; currency: string; method: string;
    fee_rate: number; fee_rate_pct: number; fee_amount: number;
    total_to_pay: number; amount_customer_receives: number;
    fee_label: string; has_fee: boolean;
  };
  const [feePreview, setFeePreview] = useState<FeePreview | null>(null);

  // Balances
  const [mobileBalanceSummary, setMobileBalanceSummary] = useState<Record<string, BalanceSummary>>({});
  const [cardBalances, setCardBalances] = useState<CardWalletRow[]>([]);
  const [loadingBalances, setLoadingBalances] = useState(false);

  // Withdrawal config (recipient phones + email)
  const [wConfig, setWConfig] = useState<any>(null);
  const [pendingRequest, setPendingRequest] = useState<any>(null);
  const [lastReview, setLastReview] = useState<any>(null);
  const [configLoading, setConfigLoading] = useState(false);

  // Flow + OTP
  const [flowStep, setFlowStep] = useState<FlowStep>('form');
  const [otpReference, setOtpReference] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Result
  const [resultMessage, setResultMessage] = useState('');
  const [resultStatus, setResultStatus] = useState('');
  const [resultReference, setResultReference] = useState('');

  // UX
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // ── Effective destination + recipient computed per channel ────────────
  const destOperator: Operator = channel === 'mobile' ? mobileOperator : cardDestOperator;
  const currency: Currency = channel === 'mobile' ? mobileCurrency : cardCurrency;
  const configuredPhone: string | null = wConfig ? (wConfig[`phone_${destOperator}`] || null) : null;

  // ── Token header for direct axios calls (card flow uses these) ────────
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // ── Load access flags ─────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.merchant_code) { setAccessLoading(false); return; }
      try {
        const methods = await subscriptionService.getActivePaymentMethods();
        if (!cancelled) {
          setHasCardAccess(methods.has_card);
          setHasMobileAccess(methods.has_mobile_money);
        }
      } catch { /* defaults stay false */ }
      finally { if (!cancelled) setAccessLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [user?.merchant_code]);

  // Default channel based on what the merchant can cash out from
  useEffect(() => {
    if (accessLoading || isLoadingEligibility) return;
    // Prefer mobile if mobile cashout is allowed; else fall back to card
    if (hasWithdrawalAccess) setChannel('mobile');
    else if (hasCardAccess) setChannel('card');
  }, [accessLoading, isLoadingEligibility, hasWithdrawalAccess, hasCardAccess]);

  // ── Balance loaders ───────────────────────────────────────────────────
  const loadMobileBalances = useCallback(async () => {
    if (!hasMobileAccess) return;
    try {
      const data = await withdrawalService.getBalanceSummary();
      setMobileBalanceSummary(data);
    } catch { /* */ }
  }, [hasMobileAccess]);

  const loadCardBalances = useCallback(async () => {
    if (!hasCardAccess) return;
    try {
      const res = await axios.get(`${API_URL}/merchants/card-wallets/balances`, { headers });
      const wallets = (res.data?.wallets || [])
        .filter((w: any) => w.mode === 'production')
        .map((w: any) => ({
          currency: w.currency as Currency,
          balance: parseFloat(w.balance),
          mode: 'production',
        }));
      setCardBalances(wallets);
    } catch { setCardBalances([]); }
  }, [hasCardAccess, headers]);

  const loadAllBalances = useCallback(async () => {
    setLoadingBalances(true);
    await Promise.all([loadMobileBalances(), loadCardBalances()]);
    setLoadingBalances(false);
  }, [loadMobileBalances, loadCardBalances]);

  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const data = await withdrawalService.getWithdrawalConfig();
      setWConfig(data?.configured ? data : null);
      setPendingRequest(data?.pending_request || null);
      setLastReview(data?.last_review || null);
    } catch {
      setWConfig(null); setPendingRequest(null); setLastReview(null);
    } finally { setConfigLoading(false); }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const result = await withdrawalService.getHistory(1, 20);
      const tx: HistoryItem[] = (result.transactions || []).map((t: any) => ({
        id: t.reference || t.id,
        amount: t.amount,
        currency: t.currency,
        method: t.method,
        reference: t.reference,
        status: mapStatus(t.status),
        created_at: t.created_at,
        source_wallets: Array.isArray(t.source_wallets) ? t.source_wallets : [],
        debit_plan: Array.isArray(t.debit_plan) ? t.debit_plan : [],
      }));
      setHistory(tx);
    } catch { setHistory([]); }
    finally { setLoadingHistory(false); }
  }, []);

  useEffect(() => {
    if (!user || accessLoading) return;
    loadAllBalances();
    loadConfig();
  }, [user, accessLoading, loadAllBalances, loadConfig]);

  // Resend timer
  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setInterval(() => setResendTimer(s => s - 1), 1000);
    return () => clearInterval(id);
  }, [resendTimer]);

  // ── Computed values ───────────────────────────────────────────────────
  const amountNumber = useMemo(() => {
    const v = Number(String(amount).replace(',', '.'));
    return Number.isFinite(v) ? Math.round(v * 100) / 100 : 0;
  }, [amount]);

  // Available balance for the currently selected source
  const availableBalance = useMemo(() => {
    if (channel === 'mobile') {
      const sum = mobileBalanceSummary[currency];
      if (!sum) return 0;
      if (sum.operator_details?.length) {
        return sum.operator_details.reduce((acc, d) => {
          const dep = parseFloat(String(d.realtime_deposit ?? 0)) || 0;
          const wth = parseFloat(String(d.realtime_withdrawal ?? 0)) || 0;
          return acc + dep + wth;
        }, 0);
      }
      return parseFloat(String(sum.total_balance ?? sum.available_for_withdrawal ?? 0)) || 0;
    }
    // card
    const w = cardBalances.find(b => b.currency === cardCurrency);
    return w ? w.balance : 0;
  }, [channel, currency, mobileBalanceSummary, cardBalances, cardCurrency]);

  // TELCO_FEE_V1 — preview fee on amount/method/currency change (mobile channel only)
  useEffect(() => {
    if (channel !== 'mobile' || !amountNumber || amountNumber <= 0) {
      setFeePreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get<FeePreview>(`${API_URL}/withdrawal/preview-fee`, {
          headers,
          params: { amount: amountNumber, currency, method: mobileOperator },
        });
        if (!cancelled) setFeePreview(res.data);
      } catch {
        if (!cancelled) setFeePreview(null);
      }
    })();
    return () => { cancelled = true; };
  }, [channel, amountNumber, currency, mobileOperator, headers]);

  const previewFeeAmount = (feePreview
    && feePreview.amount === amountNumber
    && feePreview.method === mobileOperator
    && feePreview.currency === currency) ? feePreview.fee_amount : 0;
  const totalDebit = amountNumber + previewFeeAmount;

  const canSubmit = (
    amountNumber > 0 &&
    totalDebit <= availableBalance &&
    !!configuredPhone &&
    !!destOperator
  );

  // ── Handlers ──────────────────────────────────────────────────────────
  const resetForm = useCallback(() => {
    setAmount('');
    setError(null);
    setFlowStep('form');
    setOtpReference(''); setOtpCode(''); setOtpMessage('');
    setResultMessage(''); setResultStatus(''); setResultReference('');
  }, []);

  const onChannelChange = (next: Channel) => {
    if (next === channel) return;
    setChannel(next);
    setAmount('');
    setError(null);
  };

  // Initiate — uses different APIs per channel
  const initiate = async () => {
    setError(null); setSubmitting(true);
    try {
      if (channel === 'mobile') {
        const response = await withdrawalService.initiateOperatorWithdrawal({
          amount: amountNumber,
          currency,
          method: destOperator,
          phone: configuredPhone || '',
        });
        if (response.status === 'otp_required') {
          setOtpReference(response.reference);
          setOtpMessage(response.message || 'Un code OTP a été envoyé à votre email.');
          setFlowStep('otp');
          setResendTimer(180);
        } else if (['success', 'pending'].includes(response.status)) {
          setResultMessage(response.message || 'Retrait initié avec succès.');
          setResultStatus(response.status);
          setResultReference(response.reference || '');
          setFlowStep('done');
          loadAllBalances();
        } else if (response.status === 'pending_approval') {
          setResultMessage(response.message || 'Votre retrait dépasse la limite et a été soumis pour approbation.');
          setResultStatus('pending_approval');
          setResultReference(response.reference || '');
          setFlowStep('done');
        }
      } else {
        // card channel
        const res = await axios.post(`${UNIFIED_API}/merchant-withdrawal/request-otp`, {
          amount: amountNumber, currency, method: destOperator,
        }, { headers });
        if (res.data?.success) {
          setOtpReference(res.data.reference);
          setOtpMessage(res.data.message || 'Un code OTP a été envoyé à votre email.');
          setFlowStep('otp');
          setResendTimer(180);
        } else {
          setError(res.data?.detail || 'Échec de la demande OTP');
        }
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.error_type === 'limit_exceeded') {
        const d = detail.details || {};
        setError(`Limite hebdomadaire dépassée. Limite : ${d.weekly_limit || 'N/A'} ${currency}, Déjà retiré : ${d.already_withdrawn || 0} ${currency}, Restant : ${d.remaining_limit || 0} ${currency}`);
      } else {
        setError(typeof detail === 'string' ? detail : (detail?.message || e?.message || 'Échec du retrait'));
      }
    } finally { setSubmitting(false); }
  };

  // Confirm OTP — different APIs per channel
  const confirmOTP = async () => {
    if (otpCode.length !== 6) { setError('Veuillez entrer le code OTP à 6 chiffres'); return; }
    setSubmitting(true); setError(null);
    try {
      if (channel === 'mobile') {
        const response = await withdrawalService.initiateOperatorWithdrawal({
          amount: amountNumber, currency, method: destOperator, phone: configuredPhone || '',
          otp_code: otpCode, reference: otpReference, otp_reference: otpReference,
        });
        if (['success', 'pending'].includes(response.status)) {
          setResultMessage(response.message || 'Retrait initié avec succès.');
          setResultStatus(response.status);
          setResultReference(response.reference || otpReference);
          setFlowStep('done');
          loadAllBalances();
        } else if (response.status === 'pending_approval') {
          setResultMessage(response.message || 'Votre retrait a été soumis pour approbation.');
          setResultStatus('pending_approval');
          setResultReference(response.reference || otpReference);
          setFlowStep('done');
        } else {
          setError(response.message || 'Échec de la vérification OTP');
        }
      } else {
        const res = await axios.post(`${UNIFIED_API}/merchant-withdrawal/confirm`, {
          reference: otpReference, otp_code: otpCode,
        }, { headers });
        setResultMessage(res.data?.detail || 'Retrait initié avec succès.');
        setResultStatus(mapStatus(res.data?.status));
        setResultReference(res.data?.reference || otpReference);
        setFlowStep('done');
        loadAllBalances();
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (e?.message || 'Échec du retrait'));
    } finally { setSubmitting(false); }
  };

  const resendOTP = async () => {
    if (resendTimer > 0) return;
    try {
      if (channel === 'mobile') {
        await withdrawalService.resendOTP(otpReference);
      } else {
        // Card flow — re-request OTP
        const res = await axios.post(`${UNIFIED_API}/merchant-withdrawal/request-otp`, {
          amount: amountNumber, currency, method: destOperator,
        }, { headers });
        if (res.data?.reference) setOtpReference(res.data.reference);
      }
      setResendTimer(180);
      setOtpMessage('Un nouveau code OTP a été envoyé à votre email.');
    } catch { setError('Échec du renvoi de l\'OTP'); }
  };

  // ── Loading / no-access guards ───────────────────────────────────────
  if (isLoadingEligibility || accessLoading) {
    return (
      <div className="max-w-7xl mx-auto p-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  const canCashout = hasWithdrawalAccess || hasCardAccess;
  if (!canCashout) {
    return (
      <div className="max-w-lg mx-auto mt-12 px-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-rose-50 ring-1 ring-inset ring-rose-200 flex items-center justify-center mx-auto mb-5">
              <Shield className="h-6 w-6 text-rose-600" />
            </div>
            <h2 className="text-base font-semibold text-gray-900 mb-2">{t('withdrawal.accessRequired')}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">{t('withdrawal.accessRequiredDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="w-full space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 pb-2">
        <div>
          <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight">
            {t('withdrawal.title') || 'Cashout'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('withdrawal.subtitle') || 'Send your funds to an external mobile money account'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowHistory(v => !v); if (!showHistory) loadHistory(); }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Clock className="h-3.5 w-3.5" />
            {showHistory ? (t('common.close') || 'Close') : (t('withdrawal.history') || 'History')}
          </button>
          {user?.merchant_code && (
            <div className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 ring-1 ring-inset ring-gray-200 rounded-md">
              <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                {t('withdrawal.merchant') || 'Merchant'}
              </span>
              <span className="text-[11px] font-semibold text-gray-900 font-mono">{user.merchant_code}</span>
            </div>
          )}
        </div>
      </div>

      {/* History panel toggle */}
      {showHistory ? (
        <HistoryPanel
          loading={loadingHistory}
          items={history}
          onRefresh={loadHistory}
          t={t}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Stepper header */}
          <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50/60">
            <Stepper currentStep={flowStep} t={t} />
            <button
              onClick={() => { loadAllBalances(); loadConfig(); }}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-gray-800"
              disabled={loadingBalances}
            >
              <RefreshCw className={`h-3 w-3 ${loadingBalances ? 'animate-spin' : ''}`} />
              {t('common.refresh') || 'Refresh'}
            </button>
          </div>

          <div className="p-5 space-y-5">
            {error && (
              <div className="p-3 rounded-md bg-rose-50 ring-1 ring-inset ring-rose-200 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
                <p className="text-sm text-rose-800">
                  <span className="font-semibold">{t('common.error') || 'Error'} :</span> {error}
                </p>
              </div>
            )}

            {flowStep === 'form' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                {/* SECTION 1: Channel selector */}
                <FormSection
                  step={1}
                  title={t('withdrawal.sourceWallet') || 'Source wallet'}
                  hint={t('withdrawal.sourceWalletHint') || 'Choose where the funds come from'}
                >
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <ChannelCard
                      active={channel === 'mobile'}
                      disabled={!hasWithdrawalAccess}
                      icon={<Smartphone className="h-4 w-4" />}
                      label={t('withdrawal.channelMobile')}
                      sublabel={
                        hasWithdrawalAccess
                          ? t('withdrawal.fromMobileWallet')
                          : !hasMobileAccess
                            ? t('withdrawal.mobileNoSubscription')
                            : t('withdrawal.mobileCashoutLocked')
                      }
                      cta={!hasWithdrawalAccess ? {
                        label: t('withdrawal.requestAccess'),
                        to: '/tickets',
                      } : undefined}
                      onClick={() => onChannelChange('mobile')}
                    />
                    <ChannelCard
                      active={channel === 'card'}
                      disabled={!hasCardAccess}
                      icon={<CreditCard className="h-4 w-4" />}
                      label={t('withdrawal.channelCard')}
                      sublabel={
                        hasCardAccess
                          ? t('withdrawal.fromCardWallet')
                          : t('withdrawal.cardNoSubscription')
                      }
                      cta={!hasCardAccess ? {
                        label: t('withdrawal.requestAccess'),
                        to: '/tickets',
                      } : undefined}
                      onClick={() => onChannelChange('card')}
                    />
                  </div>

                  {/* Source picker — adapts to channel */}
                  {channel === 'mobile' ? (
                    <div className="space-y-3">
                      <div>
                        <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-2">
                          {t('withdrawal.pickMobileCurrency') || 'Pick your currency'}
                        </p>
                        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
                          {(['CDF', 'USD'] as Currency[]).map((cur) => {
                            const selected = mobileCurrency === cur;
                            return (
                              <button
                                key={cur}
                                type="button"
                                onClick={() => setMobileCurrency(cur)}
                                className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${
                                  selected
                                    ? 'bg-emerald-500 text-white shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                }`}
                              >
                                {cur === 'CDF' ? 'CDF' : 'USD'}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-2">
                          {t('withdrawal.pickOperator') || 'Pick your operator'}
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                          {OPERATORS.map((op) => {
                            const selected = mobileOperator === op;
                            // Show per-operator balance from summary
                            const opBal = (mobileBalanceSummary[currency]?.operator_details || [])
                              .find((d: any) => (d.method || '').toLowerCase() === op);
                            const opAvail = opBal
                              ? (parseFloat(String(opBal.realtime_deposit ?? 0)) || 0) + (parseFloat(String(opBal.realtime_withdrawal ?? 0)) || 0)
                              : 0;
                            return (
                              <OperatorTile
                                key={op}
                                selected={selected}
                                op={op}
                                hint={fmt(opAvail, currency)}
                                onClick={() => setMobileOperator(op)}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-2">
                        {t('withdrawal.pickCurrency') || 'Pick your card wallet'}
                      </p>
                      <div className="grid grid-cols-2 gap-2.5">
                        {(['CDF', 'USD'] as Currency[]).map((cur) => {
                          const w = cardBalances.find(b => b.currency === cur);
                          const selected = cardCurrency === cur;
                          return (
                            <button
                              key={cur}
                              type="button"
                              onClick={() => setCardCurrency(cur)}
                              className={`relative p-3.5 rounded-lg border text-left transition-all ${
                                selected
                                  ? 'border-emerald-500 bg-emerald-50/40 ring-2 ring-inset ring-emerald-100'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              {selected && (
                                <span className="absolute top-2 right-2 h-4 w-4 rounded-full bg-emerald-500 flex items-center justify-center">
                                  <CheckCircle2 className="h-3 w-3 text-white" strokeWidth={3} />
                                </span>
                              )}
                              <div className="flex items-center gap-2 mb-1">
                                <div className="h-7 w-7 rounded-md bg-gray-50 ring-1 ring-inset ring-gray-200 flex items-center justify-center">
                                  <CreditCard className="h-3.5 w-3.5 text-gray-700" />
                                </div>
                                <span className={`text-xs font-semibold ${selected ? 'text-emerald-700' : 'text-gray-700'}`}>
                                  {cur} {t('withdrawal.cardWallet') || 'wallet'}
                                </span>
                              </div>
                              <p className="text-lg font-semibold text-gray-900 tabular-nums tracking-tight">
                                {fmt(w?.balance || 0, cur)}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </FormSection>

                {/* SECTION 2: Destination */}
                <FormSection
                  step={2}
                  title={t('withdrawal.destination') || 'Destination'}
                  hint={
                    channel === 'mobile'
                      ? (t('withdrawal.destMobileHint') || 'Funds are sent to the same operator')
                      : (t('withdrawal.destCardHint') || 'Pick where to send the funds')
                  }
                >
                  {channel === 'card' ? (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                      {OPERATORS.map((op) => {
                        const selected = cardDestOperator === op;
                        const phone = wConfig?.[`phone_${op}`];
                        return (
                          <OperatorTile
                            key={op}
                            selected={selected}
                            op={op}
                            hint={phone || (t('withdrawal.noPhone') || 'Not configured')}
                            hintTone={phone ? 'normal' : 'warn'}
                            onClick={() => setCardDestOperator(op)}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-3.5 rounded-md bg-gray-50 ring-1 ring-inset ring-gray-200 flex items-center gap-3">
                      <div className="h-9 w-9 rounded-md bg-white ring-1 ring-inset ring-gray-200 flex items-center justify-center overflow-hidden shrink-0">
                        <img src={OPERATOR_LOGOS[mobileOperator]} alt="" className="h-6 w-6 object-contain" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{OPERATOR_LABELS[mobileOperator]}</p>
                        <p className="text-xs text-gray-500">
                          {t('withdrawal.sameOperator') || 'Same operator as source'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Recipient phone + config status */}
                  <RecipientPanel
                    operator={destOperator}
                    configuredPhone={configuredPhone}
                    wConfig={wConfig}
                    configLoading={configLoading}
                    pendingRequest={pendingRequest}
                    lastReview={lastReview}
                    t={t}
                  />
                </FormSection>

                {/* SECTION 3: Amount */}
                <FormSection
                  step={3}
                  title={t('withdrawal.amount') || 'Amount'}
                  hint={t('withdrawal.amountHint') || 'How much do you want to send?'}
                >
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-medium text-gray-700">
                        {t('common.amount') || 'Amount'} <span className="text-gray-400">({currency})</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setAmount(availableBalance.toFixed(2))}
                        disabled={!availableBalance}
                        className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded ring-1 ring-inset ring-emerald-200 disabled:opacity-50"
                      >
                        Max
                      </button>
                    </div>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">
                        {currency === 'CDF' ? 'FC' : '$'}
                      </span>
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        max={availableBalance}
                        className="w-full pl-11 pr-3 py-3 text-lg font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all tabular-nums"
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <p className="text-[11px] text-gray-500">
                        {t('withdrawal.amountAvailable') || 'Available'} :{' '}
                        <span className="font-medium text-gray-700 tabular-nums">{fmt(availableBalance, currency)}</span>
                      </p>
                      {amountNumber > availableBalance && availableBalance > 0 && (
                        <p className="text-[11px] text-rose-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {t('withdrawal.insufficientBalance') || 'Insufficient balance'}
                        </p>
                      )}
                    </div>
                  </div>
                </FormSection>

                </div>

                {/* RIGHT COLUMN — sticky live preview + helpers */}
                <aside className="lg:col-span-1 space-y-3">
                  <div className="lg:sticky lg:top-4 space-y-3">
                    {/* Live summary */}
                    <div className={`rounded-lg border p-4 ${
                      canSubmit
                        ? 'bg-gradient-to-br from-emerald-50/60 to-white border-emerald-200'
                        : 'bg-white border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-[11px] font-semibold text-gray-700 uppercase tracking-wider">
                          {t('withdrawal.livePreview') || 'Summary'}
                        </p>
                        {canSubmit && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                            <CheckCircle2 className="h-2.5 w-2.5" />
                            {t('withdrawal.ready') || 'Ready'}
                          </span>
                        )}
                      </div>

                      <div className="space-y-2.5">
                        <SummaryRow
                          label={t('withdrawal.from') || 'From'}
                          icon={channel === 'mobile' ? <Smartphone className="h-3.5 w-3.5" /> : <CreditCard className="h-3.5 w-3.5" />}
                          value={channel === 'mobile' ? OPERATOR_LABELS[mobileOperator] : `${cardCurrency} ${t('withdrawal.cardWallet') || 'card'}`}
                        />
                        <SummaryRow
                          label={t('withdrawal.to') || 'To'}
                          logo={OPERATOR_LOGOS[destOperator]}
                          value={configuredPhone || (t('withdrawal.noPhone') || 'Not configured')}
                          mono={!!configuredPhone}
                          tone={configuredPhone ? 'normal' : 'warn'}
                        />
                        <div className="pt-2.5 border-t border-gray-100">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">
                            {t('common.amount') || 'Amount'}
                          </p>
                          <p className={`text-xl font-semibold tabular-nums tracking-tight ${
                            amountNumber > 0 ? 'text-gray-900' : 'text-gray-300'
                          }`}>
                            {amountNumber > 0 ? fmt(amountNumber, currency) : `0 ${currency}`}
                          </p>
                        </div>

                        {/* TELCO_FEE_V1 — Airtel 1.16% breakdown when applicable */}
                        {amountNumber > 0 && channel === 'mobile' && previewFeeAmount > 0 && (
                          <div className="pt-2.5 border-t border-gray-100 space-y-1.5">
                            <div className="flex justify-between text-[11px]">
                              <span className="text-gray-500">{feePreview?.fee_label || 'Frais opérateur'}</span>
                              <span className="font-mono font-medium text-amber-700">
                                + {fmt(previewFeeAmount, currency)}
                              </span>
                            </div>
                            <div className="flex justify-between pt-1.5 border-t border-dashed border-gray-200">
                              <span className="text-[11px] text-gray-700 font-semibold uppercase tracking-wider">Débité du wallet</span>
                              <span className={`font-mono font-bold text-sm ${totalDebit > availableBalance ? 'text-red-600' : 'text-gray-900'}`}>
                                {fmt(totalDebit, currency)}
                              </span>
                            </div>
                            <p className="text-[10px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-md px-2 py-1.5 mt-1.5 leading-relaxed">
                              Le canal <span className="font-semibold">{mobileOperator.toUpperCase()}</span> facture {feePreview?.fee_rate_pct.toFixed(2)}% à Moko. Ce frais est ajouté au montant débité de votre wallet ; vous recevez l'intégralité du montant transféré.
                            </p>
                          </div>
                        )}
                      </div>

                      {!canSubmit && (
                        <p className="text-[11px] text-gray-500 mt-3 leading-relaxed">
                          {amountNumber > 0 && totalDebit > availableBalance
                            ? `Solde insuffisant. Débit prévu : ${fmt(totalDebit, currency)}${previewFeeAmount > 0 ? ` (dont ${fmt(previewFeeAmount, currency)} de frais)` : ''}. Disponible : ${fmt(availableBalance, currency)}`
                            : (t('withdrawal.fillToContinue') || 'Fill in the form to continue.')}
                        </p>
                      )}
                    </div>

                    {/* How it works */}
                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                      <p className="text-[11px] font-semibold text-gray-700 uppercase tracking-wider mb-2.5">
                        {t('withdrawal.howItWorks') || 'How it works'}
                      </p>
                      <ol className="space-y-2 text-[11px] text-gray-600">
                        {[
                          t('withdrawal.howStep1') || 'Pick your source wallet (Mobile Money or Card)',
                          t('withdrawal.howStep2') || 'Confirm the destination operator and recipient',
                          t('withdrawal.howStep3') || 'Enter the amount to send and continue',
                          t('withdrawal.howStep4') || 'Verify with the OTP sent to your email',
                          t('withdrawal.howStep5') || 'Funds are released to the recipient phone',
                        ].map((step, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="h-4 w-4 rounded-full bg-gray-900 text-white text-[9px] font-bold flex items-center justify-center mt-0.5 shrink-0">
                              {i + 1}
                            </span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </aside>
              </div>
            )}

            {flowStep === 'otp' && (
              <div className="bg-blue-50/40 ring-1 ring-inset ring-blue-200 rounded-md p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="h-9 w-9 rounded-md bg-blue-50 ring-1 ring-inset ring-blue-200 text-blue-700 flex items-center justify-center shrink-0">
                    <Shield className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900">{t('withdrawal.otpTitle') || 'Verify your withdrawal'}</h3>
                    <p className="text-xs text-gray-600 mt-0.5">{otpMessage}</p>
                    <p className="text-[11px] text-gray-500 mt-1">
                      {t('withdrawal.otpFor') || 'Confirming'} :{' '}
                      <span className="font-medium text-gray-700">{fmt(amountNumber, currency)}</span>{' '}
                      {t('withdrawal.otpVia') || 'via'}{' '}
                      <span className="font-medium text-gray-700">{OPERATOR_LABELS[destOperator]}</span>
                    </p>
                  </div>
                </div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('withdrawal.enterOtp') || 'Enter the 6-digit OTP code'}</label>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  maxLength={6}
                  autoFocus
                  inputMode="numeric"
                  className="w-full px-4 py-3 text-center text-2xl tracking-[0.5em] font-mono font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-white"
                />
                <div className="flex items-center justify-between mt-3">
                  <button
                    onClick={resendOTP}
                    disabled={resendTimer > 0}
                    className={`text-xs font-medium ${resendTimer > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:underline'}`}
                  >
                    {resendTimer > 0
                      ? `${t('withdrawal.resendIn') || 'Resend in'} ${Math.floor(resendTimer / 60)}:${String(resendTimer % 60).padStart(2, '0')}`
                      : (t('withdrawal.resendOtp') || 'Resend OTP')}
                  </button>
                </div>
              </div>
            )}

            {flowStep === 'done' && (
              <div className="text-center py-6">
                <div
                  className={`h-14 w-14 rounded-full mx-auto mb-3 flex items-center justify-center ring-1 ring-inset ${
                    resultStatus === 'pending_approval'
                      ? 'bg-amber-50 ring-amber-200 text-amber-600'
                      : 'bg-emerald-50 ring-emerald-200 text-emerald-600'
                  }`}
                >
                  {resultStatus === 'pending_approval' ? <Hourglass className="h-6 w-6" /> : <CheckCircle2 className="h-6 w-6" />}
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {resultStatus === 'pending_approval'
                    ? (t('withdrawal.donePending') || 'Awaiting approval')
                    : (t('withdrawal.doneInitiated') || 'Withdrawal initiated')}
                </h3>
                <p className="text-sm text-gray-600 max-w-md mx-auto">{resultMessage}</p>
                {resultReference && (
                  <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 ring-1 ring-inset ring-gray-200 rounded-md">
                    <span className="text-[11px] text-gray-500">{t('withdrawal.referenceLabel') || 'Reference'} :</span>
                    <code className="text-xs font-mono font-semibold text-gray-900">{resultReference}</code>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer action bar */}
          <div className="px-5 py-3.5 border-t border-gray-200 bg-gray-50/60 flex items-center justify-between gap-2">
            <button
              onClick={resetForm}
              className="px-3.5 py-1.5 border border-gray-300 bg-white rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-xs font-medium"
            >
              {flowStep === 'done' ? (t('withdrawal.newCashout') || 'New cashout') : (t('withdrawal.clearForm') || 'Clear')}
            </button>
            {flowStep === 'form' && (
              <button
                onClick={initiate}
                disabled={!canSubmit || submitting}
                className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-md text-sm font-semibold transition-all shadow-sm ${
                  !canSubmit || submitting
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                }`}
              >
                {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> {t('withdrawal.sending') || 'Sending...'}</>) : (<><Banknote className="h-4 w-4" /> {t('withdrawal.cashoutBtn') || 'Continue'}</>)}
              </button>
            )}
            {flowStep === 'otp' && (
              <button
                onClick={confirmOTP}
                disabled={otpCode.length !== 6 || submitting}
                className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-md text-sm font-semibold transition-all shadow-sm ${
                  otpCode.length !== 6 || submitting
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> {t('withdrawal.confirming') || 'Confirming...'}</>) : (<><Shield className="h-4 w-4" /> {t('withdrawal.confirmCashout') || 'Confirm'}</>)}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ───────── Sub-components ───────── */

function Stepper({ currentStep, t }: { currentStep: FlowStep; t: (k: string) => string }) {
  const steps: { id: FlowStep; label: string }[] = [
    { id: 'form', label: t('withdrawal.stepDetails') || 'Details' },
    { id: 'otp',  label: t('withdrawal.stepVerify')  || 'Verify' },
    { id: 'done', label: t('withdrawal.stepConfirm') || 'Confirmation' },
  ];
  const currentIdx = steps.findIndex(s => s.id === currentStep);
  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => {
        const isActive = i === currentIdx;
        const isDone = i < currentIdx;
        return (
          <React.Fragment key={s.id}>
            <div className="flex items-center gap-1.5">
              <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                isDone ? 'bg-emerald-600 text-white' :
                isActive ? 'bg-gray-900 text-white' :
                'bg-gray-200 text-gray-500'
              }`}>
                {isDone ? <CheckCircle2 className="h-3 w-3" strokeWidth={3} /> : (i + 1)}
              </div>
              <span className={`text-xs font-medium ${
                isActive ? 'text-gray-900' : isDone ? 'text-emerald-700' : 'text-gray-400'
              }`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <ChevronRight className="h-3 w-3 text-gray-300" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function FormSection({
  step, title, hint, children,
}: { step: number; title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <div className="h-5 w-5 rounded-full bg-gray-900 text-white text-[10px] font-bold flex items-center justify-center">
          {step}
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900 leading-none">{title}</h3>
          {hint && <p className="text-[11px] text-gray-500 mt-0.5">{hint}</p>}
        </div>
      </div>
      <div className="pl-7 space-y-3">{children}</div>
    </section>
  );
}

function ChannelCard({
  active, disabled, icon, label, sublabel, cta, onClick,
}: {
  active: boolean;
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  cta?: { label: string; to: string };
  onClick: () => void;
}) {
  // Disabled card is no longer a <button> — we render a <div> so the inner
  // <Link> remains clickable (a Link inside a disabled <button> wouldn't fire).
  if (disabled) {
    return (
      <div className="relative p-4 rounded-lg border border-amber-200 bg-amber-50/40 cursor-not-allowed">
        <span className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 ring-1 ring-inset ring-amber-200">
          <Shield className="h-2.5 w-2.5" />
          Locked
        </span>
        <div className="h-8 w-8 rounded-md flex items-center justify-center mb-2.5 bg-amber-100 text-amber-700">
          {icon}
        </div>
        <p className="text-sm font-semibold text-gray-700">{label}</p>
        <p className="text-[11px] text-amber-800 mt-0.5 leading-snug">{sublabel}</p>
        {cta && (
          <Link
            to={cta.to}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:text-amber-900 hover:underline"
          >
            <ChevronRight className="h-3 w-3" />
            {cta.label}
          </Link>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative p-4 rounded-lg border text-left transition-all ${
        active
          ? 'border-emerald-500 bg-gradient-to-br from-emerald-50/60 to-white ring-2 ring-inset ring-emerald-100 shadow-sm'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
      }`}
    >
      {active && (
        <span className="absolute top-2.5 right-2.5 h-4 w-4 rounded-full bg-emerald-500 flex items-center justify-center">
          <CheckCircle2 className="h-3 w-3 text-white" strokeWidth={3} />
        </span>
      )}
      <div className={`h-8 w-8 rounded-md flex items-center justify-center mb-2.5 ${
        active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
      }`}>
        {icon}
      </div>
      <p className={`text-sm font-semibold ${active ? 'text-emerald-900' : 'text-gray-900'}`}>{label}</p>
      <p className="text-[11px] text-gray-500 mt-0.5">{sublabel}</p>
    </button>
  );
}

function OperatorTile({
  selected, op, hint, hintTone = 'normal', onClick,
}: {
  selected: boolean;
  op: Operator;
  hint?: string;
  hintTone?: 'normal' | 'warn';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative p-3 rounded-md border text-center transition-all ${
        selected
          ? 'border-emerald-500 bg-emerald-50/40 ring-2 ring-inset ring-emerald-100'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      {selected && (
        <span className="absolute top-1.5 right-1.5 h-3.5 w-3.5 rounded-full bg-emerald-500 flex items-center justify-center">
          <CheckCircle2 className="h-3 w-3 text-white" strokeWidth={3} />
        </span>
      )}
      <div className="flex flex-col items-center gap-1.5">
        <img src={OPERATOR_LOGOS[op]} alt={op} className="h-7 w-7 object-contain" />
        <span className={`text-[11px] font-medium ${selected ? 'text-emerald-700' : 'text-gray-700'}`}>
          {OPERATOR_LABELS[op]}
        </span>
        {hint && (
          <span className={`text-[10px] font-mono truncate max-w-full ${
            hintTone === 'warn' ? 'text-amber-600' : 'text-gray-500'
          }`}>{hint}</span>
        )}
      </div>
    </button>
  );
}

function RecipientPanel({
  operator, configuredPhone, wConfig, configLoading, pendingRequest, lastReview, t,
}: {
  operator: Operator;
  configuredPhone: string | null;
  wConfig: any;
  configLoading: boolean;
  pendingRequest: any;
  lastReview: any;
  t: (k: string) => string;
}) {
  if (configLoading) {
    return (
      <div className="p-3 rounded-md bg-gray-50 ring-1 ring-inset ring-gray-200 flex items-center gap-2 mt-3">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
        <span className="text-xs text-gray-500">{t('withdrawal.loadingConfig') || 'Loading configuration...'}</span>
      </div>
    );
  }

  if (!wConfig) {
    return (
      <div className="space-y-2 mt-3">
        <div className="p-3 rounded-md bg-amber-50 ring-1 ring-inset ring-amber-200 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-semibold text-amber-800">{t('withdrawal.configRequired') || 'Configuration required'}</p>
            <p className="text-[11px] text-amber-700 mt-0.5">{t('withdrawal.configRequiredDesc') || 'Set up your recipient phones before cashing out.'}</p>
          </div>
        </div>
        <Link to="/cashout-settings" className="block w-full text-center px-3 py-2 rounded-md border border-dashed border-emerald-300 text-emerald-700 text-xs font-semibold hover:bg-emerald-50">
          {t('withdrawal.configurePhones') || 'Configure recipient numbers'}
        </Link>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2">
      {pendingRequest && (
        <div className="p-3 rounded-md bg-amber-50 ring-1 ring-inset ring-amber-200 flex items-start gap-2">
          <Clock className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-amber-900">{t('withdrawal.configPendingTitle') || 'Pending change request'}</p>
            <p className="text-[11px] text-amber-800 mt-0.5">{t('withdrawal.configPendingShort') || 'Your last edits are awaiting backoffice approval.'}</p>
          </div>
        </div>
      )}
      {lastReview?.status === 'rejected' && !pendingRequest && (
        <div className="p-3 rounded-md bg-rose-50 ring-1 ring-inset ring-rose-200 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-rose-900">{t('withdrawal.configRejectedTitle') || 'Last request rejected'}</p>
            {lastReview.rejection_reason && <p className="text-[11px] text-rose-800 mt-0.5">{lastReview.rejection_reason}</p>}
          </div>
        </div>
      )}
      <div className={`p-3 rounded-md flex items-center justify-between ring-1 ring-inset gap-2 ${
        configuredPhone ? 'bg-emerald-50/40 ring-emerald-200' : 'bg-amber-50 ring-amber-200'
      }`}>
        <div className="flex items-center gap-2.5 min-w-0">
          <Smartphone className={`h-4 w-4 shrink-0 ${configuredPhone ? 'text-emerald-600' : 'text-amber-600'}`} />
          {configuredPhone ? (
            <p className="text-sm text-gray-800">
              {t('withdrawal.cashoutTo') || 'Will be sent to'}{' '}
              <span className="font-semibold text-gray-900 font-mono">{configuredPhone}</span>{' '}
              <span className="text-gray-500">({OPERATOR_LABELS[operator]})</span>
            </p>
          ) : (
            <p className="text-sm text-amber-700">
              {t('withdrawal.noPhoneFor') || 'No phone configured for'} {OPERATOR_LABELS[operator]}
            </p>
          )}
        </div>
        <Link
          to="/cashout-settings"
          className={`text-[11px] font-medium hover:underline shrink-0 inline-flex items-center gap-1 ${
            configuredPhone ? 'text-emerald-700' : 'text-amber-700'
          }`}
        >
          <Settings className="h-3 w-3" />
          {configuredPhone ? (t('withdrawal.modify') || 'Edit') : (t('withdrawal.configure') || 'Set up')}
        </Link>
      </div>
    </div>
  );
}

function SummaryRow({
  label, value, icon, logo, mono, tone = 'normal',
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  logo?: string;
  mono?: boolean;
  tone?: 'normal' | 'warn';
}) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="h-8 w-8 rounded-md bg-gray-50 ring-1 ring-inset ring-gray-200 flex items-center justify-center overflow-hidden shrink-0">
        {logo ? <img src={logo} alt="" className="h-5 w-5 object-contain" /> : <span className="text-gray-600">{icon}</span>}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
        <p className={`text-xs font-medium truncate ${
          tone === 'warn' ? 'text-amber-700' : 'text-gray-900'
        } ${mono ? 'font-mono' : ''}`}>
          {value}
        </p>
      </div>
    </div>
  );
}

function HistoryPanel({
  loading, items, onRefresh, t,
}: { loading: boolean; items: HistoryItem[]; onRefresh: () => void; t: (k: string) => string }) {
  const statusMeta: Record<UIWithdrawalStatus, { pill: string; dot: string; label: string }> = {
    successful: { pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500', label: 'Succès' },
    failed:     { pill: 'bg-rose-50 text-rose-700 ring-rose-200',         dot: 'bg-rose-500',    label: 'Échoué' },
    processing: { pill: 'bg-blue-50 text-blue-700 ring-blue-200',         dot: 'bg-blue-500',    label: 'En cours' },
    pending:    { pill: 'bg-amber-50 text-amber-700 ring-amber-200',      dot: 'bg-amber-500',   label: 'En attente' },
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">{t('withdrawal.transactionHistory') || 'Transaction history'}</h3>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 ring-1 ring-inset ring-gray-200 rounded-md"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {t('common.refresh') || 'Refresh'}
        </button>
      </div>
      <div className="p-5">
        {loading ? (
          <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-14 bg-gray-100 rounded-md animate-pulse" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-10">
            <div className="h-10 w-10 mx-auto rounded-full bg-gray-50 ring-1 ring-inset ring-gray-200 flex items-center justify-center mb-2">
              <Clock className="h-4 w-4 text-gray-400" />
            </div>
            <p className="text-sm text-gray-700 font-medium">{t('withdrawal.noWithdrawals') || 'No withdrawals yet'}</p>
            <p className="text-xs text-gray-500 mt-0.5">{t('withdrawal.startFirst') || 'Your withdrawals will appear here.'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.slice(0, 20).map((item) => {
              const s = statusMeta[item.status];
              const opKey = (item.method || '').toLowerCase().replace('-', '') as Operator;
              const logo = OPERATOR_LOGOS[opKey];
              return (
                <div key={item.id} className="group flex items-center justify-between gap-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50/60 transition-colors">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="h-8 w-8 rounded-md bg-gray-50 ring-1 ring-inset ring-gray-200 flex items-center justify-center overflow-hidden shrink-0">
                      {logo ? <img src={logo} alt="" className="h-5 w-5 object-contain" /> : <Banknote className="h-4 w-4 text-gray-500" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-900 tabular-nums">
                          {fmt(item.amount, item.currency as Currency)}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ring-1 ring-inset ${s.pill}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                          {s.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
                        <span className="capitalize">{item.method}</span>
                        <span>·</span>
                        <span>{fmtDate(item.created_at)}</span>
                      </div>
                      {(() => {
                        // Show a hint when the actual debit wallet(s) differ
                        // from the requested operator — biggest-first can
                        // fan out or fall over to another wallet.
                        const wallets = item.source_wallets || [];
                        const requested = (item.method || '').toLowerCase();
                        const differs = wallets.length > 0 && !(wallets.length === 1 && wallets[0].toLowerCase() === requested);
                        if (!differs) return null;
                        const plan = item.debit_plan || [];
                        const summary = wallets.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(', ');
                        const tooltip = plan.length > 0
                          ? plan.map(p => `${p.method}: ${fmt(p.amount, item.currency as Currency)}`).join('\n')
                          : `Débité depuis ${summary}`;
                        return (
                          <div className="flex items-center gap-1 mt-1 text-[10.5px] text-amber-800 bg-amber-50 ring-1 ring-inset ring-amber-200 rounded px-1.5 py-0.5 w-fit" title={tooltip}>
                            <span className="font-semibold">Wallet source :</span>
                            <span className="capitalize">{summary}</span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <code className="hidden sm:inline-block text-[10px] text-gray-500 font-mono">{item.reference}</code>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Keep XCircle import referenced for tree-shaking safety (used by HistoryRow status enum)
void XCircle;

export default Withdrawal;
